import React, { useEffect, useState } from 'react';
import api from '../api';

const rub = kopecks => new Intl.NumberFormat('ru-RU', {
  style: 'currency', currency: 'RUB', maximumFractionDigits: 0,
}).format((kopecks || 0) / 100);

const STATUS = {
  active: ['Можно работать', '#0e9f6e'],
  warning: ['Есть предупреждение', '#e08a00'],
  limited: ['Часть действий закрыта', '#e08a00'],
  penalty: ['Включён повышенный тариф', '#e04848'],
  blocked: ['Работа остановлена до оплаты', '#e04848'],
};

const ARTICLES = {
  royalty: 'Роялти',
  marketing: 'Маркетинг',
  equipment_rent: 'Аренда оборудования',
  space_rent: 'Аренда помещения',
  franchise_fee: 'Паушальный взнос',
  subscription: 'Подписка',
  moysklad_fee: 'МойСклад',
  comm_fee: 'Связь',
  accounting_fee: 'Бухгалтерия',
  card_fee: 'Карты',
  batrek_fee: 'Батрек',
  telematics_fee: 'Телематика',
  penalty: 'Повышенный тариф',
  adjustment: 'Корректировка',
};

const BILL = {
  open: 'Счёт выставлен',
  partial: 'Оплачен частично',
  paid: 'Оплачен',
  overdue: 'Просрочен',
};

function storedRole() {
  try {
    return JSON.parse(localStorage.getItem('auth_user') || '{}').role || '';
  } catch {
    return '';
  }
}

export default function PartnerOs() {
  const [period, setPeriod] = useState(new Date().toISOString().slice(0, 7));
  const [cabinet, setCabinet] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [serviceMessage, setServiceMessage] = useState('');
  const manager = ['admin', 'manager'].includes(storedRole());

  const load = () => {
    setLoading(true);
    setError('');
    api.get('/partner-os/cabinet', { params: { period } })
      .then(r => setCabinet(r.data))
      .catch(err => setError(err.response?.data?.detail || 'Кабинет не открылся'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [period]);

  const prepare = async () => {
    setServiceMessage('Собираю данные...');
    setError('');
    try {
      await api.post('/partner-os/seed');
      await api.post('/partner-os/sync/period-stats', null, { params: { period } });
      await api.post('/partner-os/billing/runs', { period });
      setServiceMessage('Данные для кабинета обновлены');
      load();
    } catch (err) {
      setServiceMessage('');
      setError(err.response?.data?.detail ? JSON.stringify(err.response.data.detail) : err.message);
    }
  };

  const partners = cabinet?.partners || [];
  const debt = partners.reduce((sum, item) => sum + (item.billing?.debt_kopecks || 0), 0);
  const revenue = partners.reduce((sum, item) => sum + (item.month_revenue_kopecks || 0), 0);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, alignItems: 'flex-end', marginBottom: 18 }}>
        <div>
          <div style={{ color: '#00a7dc', fontSize: 12, fontWeight: 800, letterSpacing: '0.06em' }}>КАБИНЕТ ПАРТНЁРА</div>
          <h1 style={{ margin: '6px 0 0' }}>Мои точки</h1>
          <div style={{ color: 'var(--text2)', marginTop: 6 }}>Выручка, счёт управляющей компании и что нужно оплатить.</div>
        </div>
        <input value={period} onChange={e => setPeriod(e.target.value)} style={field} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(160px, 1fr))', gap: 12, marginBottom: 18 }}>
        <Metric label="Выручка за месяц" value={rub(revenue)} color="#0e9f6e" />
        <Metric label="К оплате" value={rub(debt)} color={debt > 0 ? '#e04848' : '#0e9f6e'} />
        <Metric label="Точек" value={partners.reduce((sum, item) => sum + item.branches.length, 0)} color="#00a7dc" />
      </div>

      {loading && <div style={{ color: 'var(--text2)' }}>Загружаю кабинет...</div>}
      {error && <div style={{ color: '#e04848', marginBottom: 12 }}>{String(error)}</div>}
      {!loading && partners.length === 0 && (
        <div style={card}>
          {manager
            ? 'Справочник ещё пуст. Нажмите «Подготовить кабинет», чтобы собрать партнёров и счета из текущих данных.'
            : 'Вам ещё не назначены точки. Попросите администратора открыть доступ в партнёрском портале.'}
        </div>
      )}

      {partners.map(partner => {
        const [statusLabel, statusColor] = STATUS[partner.access_status] || STATUS.active;
        return (
          <section key={partner.id} style={{ ...card, marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
              <div>
                <h2 style={{ margin: 0, fontSize: 20 }}>{partner.name}</h2>
                <div style={{ color: 'var(--text2)', marginTop: 4 }}>{partner.network.toUpperCase()}</div>
              </div>
              <span style={{ color: statusColor, fontWeight: 800 }}>{statusLabel}</span>
            </div>
            {partner.access_reason && <div style={{ marginTop: 8, color: 'var(--text2)' }}>{partner.access_reason}</div>}

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 10, marginTop: 14 }}>
              {partner.branches.map(branch => (
                <div key={branch.id} style={{ background: 'var(--bg3)', borderRadius: 10, padding: 12 }}>
                  <div style={{ fontWeight: 800 }}>{branch.name}</div>
                  <div style={{ marginTop: 10, color: 'var(--text2)', fontSize: 12 }}>Выручка</div>
                  <div style={{ fontSize: 22, fontWeight: 800, color: '#0e9f6e' }}>{rub(branch.revenue_kopecks)}</div>
                  <div style={{ marginTop: 8, color: 'var(--text2)', fontSize: 12 }}>Предыдущий месяц</div>
                  <div style={{ fontWeight: 700 }}>{branch.prev_revenue_kopecks ? rub(branch.prev_revenue_kopecks) : '—'}</div>
                </div>
              ))}
            </div>

            <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid var(--border)' }}>
              <div style={{ fontWeight: 800, marginBottom: 8 }}>Счёт за {period}</div>
              {partner.billing ? (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                    <span>{BILL[partner.billing.status] || partner.billing.status}</span>
                    <b style={{ color: partner.billing.debt_kopecks > 0 ? '#e04848' : '#0e9f6e' }}>{rub(partner.billing.debt_kopecks)}</b>
                  </div>
                  <div style={{ color: 'var(--text2)', fontSize: 12, marginTop: 4 }}>Оплатить до {partner.billing.due_on}</div>
                  <div style={{ marginTop: 8 }}>
                    {partner.billing.articles.map(article => (
                      <div key={article.code} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '4px 0' }}>
                        <span>{ARTICLES[article.code] || article.code}</span>
                        <span>{rub(article.amount_kopecks)}</span>
                      </div>
                    ))}
                  </div>
                </>
              ) : <div style={{ color: 'var(--text2)' }}>Счёт за этот месяц ещё не рассчитан.</div>}
            </div>
          </section>
        );
      })}

      {manager && (
        <div style={{ ...card, marginTop: 8 }}>
          <div style={{ fontWeight: 800, marginBottom: 8 }}>Подготовка данных</div>
          <button onClick={prepare} style={button}>Подготовить кабинет</button>
          {serviceMessage && <div style={{ marginTop: 8, color: 'var(--text2)' }}>{serviceMessage}</div>}
        </div>
      )}
    </div>
  );
}

function Metric({ label, value, color }) {
  return (
    <div style={card}>
      <div style={{ color: 'var(--text2)', fontSize: 12 }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 800, color, marginTop: 6 }}>{value}</div>
    </div>
  );
}

const card = { background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 14, padding: 16 };
const field = { padding: '10px 12px', borderRadius: 10, border: '1px solid var(--border)', background: 'var(--bg2)', color: 'var(--text1)' };
const button = { border: 'none', borderRadius: 8, padding: '10px 14px', background: 'var(--accent)', color: '#fff', fontWeight: 800, cursor: 'pointer' };
